import EventDTO from '../models/event';
import type { FeaturedBusiness } from '../config/featuredBusinesses';
import compareEventsByPriority from './compareEventsByPriority';
import { formatBannerDate, formatBannerDateRange } from './formatBannerDate';

export interface FeaturedSlide {
  key: string;
  href: string;
  title: string;
  subtitle?: string;
  dateLabel: string;
  place: string;
  bannerImage: string;
  ctaLabel: string;
}

type BannerEventField = 'slug' | 'name' | 'date' | 'place' | 'banner_image'
| 'banner_display' | 'priority' | 'business_id' | 'custom_ticket_label' | 'is_sold_out';

type BannerEvent = Pick<EventDTO, BannerEventField>;

const eventCtaLabel = (customTicketLabel?: string | null) => (
  customTicketLabel === 'registro' ? 'Registrarse' : 'Comprar entradas'
);

const eventSlide = (event: BannerEvent): FeaturedSlide => ({
  key: event.slug,
  href: `/events/${event.slug}`,
  title: event.name,
  dateLabel: event.date ? formatBannerDate(event.date) : '',
  place: event.place,
  bannerImage: event.banner_image,
  ctaLabel: eventCtaLabel(event.custom_ticket_label),
});

export interface FeaturedBusinessGroup {
  key: string;
  businessId: string;
  href: string;
  title: string;
  subtitle?: string;
  dateLabel: string;
  place: string;
  bannerImage: string;
  ctaLabel: string;
  /** Every one of its events is sold out. */
  isSoldOut: boolean;
  /** Priority and first date of its events, to rank it against other slides. */
  priority?: number;
  date: string;
}

// `events` must already be the public list sorted with compareEventsByPriority.
// Each featured business with events in the list becomes one group that links
// to its page; businesses with no events left produce nothing, so the group
// expires on its own.
export const buildFeaturedBusinessGroups = <T extends BannerEvent>(
  events: T[],
  featuredBusinesses: FeaturedBusiness[],
): FeaturedBusinessGroup[] => featuredBusinesses.flatMap((business) => {
    const businessEvents = events.filter((e) => e.business_id === business.businessId);
    if (businessEvents.length === 0) {
      return [];
    }
    const [top] = businessEvents;
    const places = new Set(businessEvents.map((e) => e.place));
    const firstDate = businessEvents
      .map((e) => e.date)
      .reduce((a, b) => (new Date(a) <= new Date(b) ? a : b));
    return [{
      key: `business-${business.slug}`,
      businessId: business.businessId,
      href: `/productoras/${business.slug}`,
      title: business.name,
      subtitle: business.subtitle,
      dateLabel: formatBannerDateRange(businessEvents.map((e) => e.date)),
      place: business.place ?? (places.size === 1 ? top.place : ''),
      bannerImage: business.bannerImage ?? top.banner_image,
      ctaLabel: business.ctaLabel ?? 'Ver eventos',
      isSoldOut: businessEvents.every((e) => e.is_sold_out),
      priority: top.priority,
      date: firstDate,
    }];
  });

// Each featured business collapses into one slide and its events leave the
// banner (the grid shows them as a single card too). The business slide
// competes in the order with its best event's priority.
const buildFeaturedSlides = (
  events: BannerEvent[],
  featuredBusinesses: FeaturedBusiness[] = [],
): FeaturedSlide[] => {
  const groups = buildFeaturedBusinessGroups(events, featuredBusinesses);
  const groupedBusinessIds = new Set(groups.map((g) => g.businessId));
  const ranked: { priority?: number; date: string; slide: FeaturedSlide }[] = groups.map((g) => ({
    priority: g.priority,
    date: g.date,
    slide: {
      key: g.key,
      href: g.href,
      title: g.title,
      subtitle: g.subtitle,
      dateLabel: g.dateLabel,
      place: g.place,
      bannerImage: g.bannerImage,
      ctaLabel: g.ctaLabel,
    },
  }));

  // The "no event is marked for the banner, show them all" fallback looks at
  // the full list: if only the grouped business had banner events, the rest
  // must not flood the banner.
  const hasBannerEvents = events.some((e) => e.banner_display);
  const source = events.filter((e) => !groupedBusinessIds.has(e.business_id)
    && (!hasBannerEvents || e.banner_display));
  source.forEach((event) => {
    ranked.push({ priority: event.priority, date: event.date, slide: eventSlide(event) });
  });

  return ranked.sort(compareEventsByPriority).map((item) => item.slide);
};

export default buildFeaturedSlides;
